import type { OperationVariables, QueryResult } from "@apollo/client";
import { useQuery } from "@apollo/client";
import FinancialBusinessSegmentsQuery from "components/stocks/modern/queries/FinancialBusinessSegmentsQuery";
import FinancialGeographicSegmentsQuery from "components/stocks/modern/queries/FinancialGeographicSegmentsQuery";
import type {
  IFinancialBusinessSegmentsQuery,
  IFinancialBusinessSegmentsQueryVariables,
} from "components/stocks/modern/queries/__generated__/FinancialBusinessSegmentsQuery.generated";
import type {
  IFinancialGeographicSegmentsQuery,
  IFinancialGeographicSegmentsQueryVariables,
} from "components/stocks/modern/queries/__generated__/FinancialGeographicSegmentsQuery.generated";
import { invariant } from "outvariant";
import type { ReactElement } from "react";

type ChildrenProps = {
  error: Error | null;
  loading: boolean;
  businessSegments: IFinancialBusinessSegmentsQuery["financialBusinessSegments"];
  geographicSegments: IFinancialGeographicSegmentsQuery["financialGeographicSegments"];
};

interface OperatingSegmentsDataProviderProps {
  tickerSymbols: string[];
  children: (props: ChildrenProps) => ReactElement | null;
}

export default function OperatingSegmentsDataProvider({ tickerSymbols, children }: OperatingSegmentsDataProviderProps) {
  invariant(tickerSymbols.length > 0, "At least one ticker symbol must be provided");
  const businessQueryResult = useQuery<IFinancialBusinessSegmentsQuery, IFinancialBusinessSegmentsQueryVariables>(
    FinancialBusinessSegmentsQuery,
    {
      skip: tickerSymbols.length === 0,
      variables: {
        tickerSymbol: tickerSymbols[0],
      },
    },
  );
  const geographicQueryResult = useQuery<IFinancialGeographicSegmentsQuery, IFinancialGeographicSegmentsQueryVariables>(
    FinancialGeographicSegmentsQuery,
    {
      skip: tickerSymbols.length === 0,
      variables: {
        tickerSymbol: tickerSymbols[0],
      },
    },
  );
  const businessData = currentOrPrevious(businessQueryResult);
  const geographicData = currentOrPrevious(geographicQueryResult);

  return children({
    error: businessQueryResult.error || geographicQueryResult.error || null,
    loading: businessQueryResult.loading || geographicQueryResult.loading,
    businessSegments: businessData?.financialBusinessSegments || null,
    geographicSegments: geographicData?.financialGeographicSegments || null,
  });
}

function currentOrPrevious<T, V extends OperationVariables>(queryResult: QueryResult<T, V>): T | null {
  return (queryResult.loading && !queryResult.data ? queryResult.previousData : queryResult.data) || null;
}
